import {
    actionAddSceneItem,
    actionAddScheduleEvent,
    actionGroupSceneItems,
    actionRemoveSceneBlocks,
    actionRemoveScheduleEvent,
    actionUngroupSceneItem,
    actionUpdateCategoryValue,
    actionUpdateSceneItem,
    actionUpdateSceneItemGroup,
    actionUpdateScenePlayer,
    actionUpdateScheduleEvent,
} from "../actions/user.actions";

/**
 * The scene-edit actions ngrx-wieder records an undo step for (its `allowedActionTypes`).
 *
 * Lives in its own module because BOTH sides of the history feature need it and neither can own it:
 * user.reducers.ts configures wieder with it, and StoreSelectorsService.dispatch() uses it to decide which
 * actions to stamp the drill path onto (see IDrillPath). Importing the list from user.reducers would drag the
 * whole reducer graph into the service.
 */
export const UNDOABLE_ACTION_TYPES: ReadonlyArray<string> = [
    actionAddSceneItem.type,
    actionRemoveSceneBlocks.type,
    actionUpdateSceneItem.type,
    actionUpdateSceneItemGroup.type,
    actionGroupSceneItems.type,
    actionUngroupSceneItem.type,
    actionUpdateScenePlayer.type,
    actionUpdateCategoryValue.type,
    actionAddScheduleEvent.type,
    actionUpdateScheduleEvent.type,
    actionRemoveScheduleEvent.type,
];

/**
 * Where the user was drilled to when an undoable edit was dispatched, so UNDO/REDO can put them back there and
 * the reverted change is actually visible. Stamped onto the action by StoreSelectorsService.dispatch() and kept
 * on the history step by wieder's trackActionPayload; read back by getHistoryTarget().
 *
 * The drill position is TWO independent dimensions and neither alone is enough:
 *  - `crumbs`     - the nested-scene trail (config.uiSceneConfig.breadcrumbs). The only dimension in the store.
 *  - `editTarget` - the IN-scene position: group drill, focused component edit, or a component inside a
 *                   playlist/collection page. Held ONLY in ContainerService; its breadcrumbs are derived live
 *                   from it by getSceneItemPathCrumbs, so if it is lost the crumbs for that level vanish too.
 *
 * A group is not its own scene record - it is a <Group> block inside its parent scene - so an edit made inside a
 * group carries the PARENT scene's sceneId. Without editTarget there is nothing to distinguish "edited the group's
 * child" from "edited the scene", which is why the trail cannot be re-derived from ids alone and must be recorded.
 *
 * Scalars and small flat objects only: trackActionPayload retains this for the life of every history step, so a
 * reference into the scene graph would pin it in memory.
 */
export interface IDrillPath {
    /** the nested-scene breadcrumb trail at dispatch time ([] when at a root scene) */
    crumbs: Array<any>;
    /** ContainerService.componentEditTarget at dispatch time (null when not in a component/group edit) */
    editTarget: { sceneId: any, componentId: any, regionWidth?: any, regionHeight?: any, needsToolbox?: boolean, fitComponent?: boolean, groupEdit?: boolean } | null;
    /** ContainerService.editorMode at dispatch time ('componentMode' while drilled into a component/group) */
    editorMode: any;
    /** the scene record on screen at dispatch time */
    sceneId: any;
}

/** Property stamped onto an undoable action carrying its IDrillPath. Underscored: it is history metadata, not payload. */
export const DRILL_PATH_KEY = "__drill";
